// src/data/claimantChannel.js
//
// T5 item 8 (2026-10-15) - RULING 29: THE CLAIMANT'S ALWAYS-OPEN CHANNEL.
// Ruling 29 (data\YCP_ELEVATION_RULES_SPEC_2026-08-26.md:18; YCP_ONBOARDING_REQUIREMENTS_2026-08-30.md:207): after
// elevation the claimant keeps read, an append-only channel, and the right to add an exhibit. The channel carries
// REQUEST, QUESTION and COMPLAINT as three distinct types, with tracked obligation and ages shown on open items, and a
// COMPLAINT routes to the holding office AND ONE LEVEL ABOVE (YCP_CONSOLIDATED_RECORD_2026-09-14.md:1109 - the DFR
// route: "ruling 29 already routes a complaint one level above the holding office").
//
// PURE, like factsActs.js: no firebase, no clock. scripts\sync-shared-modules.js projects it into
// functions\shared\claimantChannel.js, so the `channelItem` Cloud Function and the claim page decide with ONE definition.
//
// WHAT AN ITEM IS (claims/{id}/channelItems/{auto}, written ONLY by the channelItem function - the rules refuse every
// client write):
//   { type, text, byUid, byName, at, heldBy, routedTo[], status: 'open'|'answered', answer?, answeredAt?, answeredBy? }
// APPEND-ONLY. An answer is added to the item by the holding office; neither the item nor the answer is ever deleted.
//
// WHERE IT GOES. The holding office is the claim's rung (claimLadder.js LADDER .office): 'lc' or 'gc'.
//   request / question  -> the holding office only.
//   complaint, LC holds -> the Local Chairperson AND the General Chairperson's office (one level above).
//   complaint, GC holds -> the General Chairperson's office. The office above a General Chairperson is not on this
//                          platform; the claimant is TOLD so in the result (aboveNotOnPlatform), never left to assume
//                          the complaint went higher than it did. The administrator reads every notice.
import { LADDER } from './claimLadder';
import { factsClaimant } from './factsActs';

export const REQUEST = 'request';
export const QUESTION = 'question';
export const COMPLAINT = 'complaint';
export const CHANNEL_TYPES = [REQUEST, QUESTION, COMPLAINT];
// T5 item 9 (2026-10-16) - RULING 139: "A REPRESENTATIVE MAY CAPTURE A MEMBER'S VERBAL INSTRUCTION AS TEXT; THE PLATFORM
// SURFACES IT BACK TO THE MEMBER AS A VISIBLE DATED ITEM THROUGH THE RULING-29 CHANNEL; SURFACING IS MANDATORY AND AUTOMATIC -
// IF IT WAS NOT SURFACED, IT WAS NOT CAPTURED" (data\YCP_RULINGS_2026-09-11.md, heading 139). The fourth item type runs the
// other way: the office holding the claim writes it, the CLAIMANT is told in the same transaction, and the claimant is the
// one who answers - confirming it or saying what he actually said. A claim with no claimant account to tell is refused:
// an instruction that cannot be surfaced is not captured.
export const INSTRUCTION = 'instruction';
export const HOW_GIVEN = { phone: 'by telephone', in_person: 'in person', other: 'another way (say how in the text)' };
export const TYPE_LABELS = { request: 'Request', question: 'Question', complaint: 'Complaint', instruction: 'Your instruction, as your representative recorded it' };
export const TEXT_MAX = 4000;
export const NOTICE_KIND = 'claimant_channel';

const s = (v) => (v == null ? '' : String(v)).trim();

// 'lc' | 'gc' | null (draft, off-ladder, unknown).
export function holdingOffice(claim) {
  const st = s(claim && claim.status);
  const rung = LADDER.find((r) => r.key === st);
  return rung && (rung.office === 'lc' || rung.office === 'gc') ? rung.office : null;
}

// Who may post: the claimant, by the same stricter test the facts act uses (factsActs.factsClaimant).
export function channelClaimant(claim, caller) {
  return factsClaimant(claim, caller);
}

export function validateItem(input, claim) {
  const i = input || {};
  const problems = [];
  const type = s(i.type);
  const text = typeof i.text === 'string' ? i.text.trim() : '';
  if (CHANNEL_TYPES.indexOf(type) === -1) problems.push('The item must be a request, a question or a complaint.');
  if (!text) problems.push('Write what you are asking or raising.');
  else if (text.length > TEXT_MAX) problems.push('The text is longer than ' + TEXT_MAX + ' characters.');
  if (!holdingOffice(claim)) problems.push('The channel opens once the claim is filed and held by an office.');
  return { ok: problems.length === 0, problems, value: { type, text } };
}

// The offices an item reaches, in order. Each is { audience, scopeKey?|committee? }.
export function routeItem(claim, type) {
  const c = claim || {};
  const held = holdingOffice(c);
  const lc = { audience: 'local_chair', scopeKey: s(c.scopeKey) };
  const gc = { audience: 'general_chair', committee: s(c.committee) };
  const routes = held === 'lc' ? [lc] : held === 'gc' ? [gc] : [];
  if (type === COMPLAINT && held === 'lc') routes.push(gc);
  const unaddressed = [];
  routes.forEach((r) => {
    if (r.audience === 'local_chair' && (!r.scopeKey || /\|UNROUTED$/.test(r.scopeKey))) unaddressed.push('local_chair: the claim is not routed to a Local Chairperson');
    if (r.audience === 'general_chair' && !r.committee) unaddressed.push('general_chair: the claim carries no committee');
  });
  return { heldBy: held, routes, aboveNotOnPlatform: type === COMPLAINT && held === 'gc', unaddressed };
}

// The item and its notices. at = ISO from the caller (the function stamps the server time on the stored item).
export function planItem({ claim, claimId, input, caller, at }) {
  const c = claim || {};
  const r = routeItem(c, input.type);
  const label = s(c.claimNumber) || s(c.title) || s(claimId).slice(0, 8).toUpperCase();
  const who = s(caller && caller.name) || s(c.claimantName) || 'The claimant';
  const item = {
    type: input.type, text: input.text, byUid: s(caller && caller.uid), byName: who,
    heldBy: r.heldBy, routedTo: r.routes.map((x) => x.audience), status: 'open',
  };
  const title = TYPE_LABELS[input.type] + ' from the claimant: ' + label;
  const body = who + ' sent a ' + input.type + ' on claim ' + label + (s(at) ? ' on ' + s(at).slice(0, 10) : '') + ':\n\n'
    + input.text + '\n\n'
    + (input.type === COMPLAINT && r.heldBy === 'lc'
      ? 'This is a complaint. Under ruling 29 it goes to the office holding the claim and one level above it.'
      : 'Answer it on the claim page; it shows as open, with its age, until it is answered.');
  const notices = r.routes.map((x) => Object.assign({
    kind: NOTICE_KIND, claimId: s(claimId), committee: s(c.committee), scopeKey: s(c.scopeKey),
    recipientUid: null, channel: 'in_app', title, body, actorName: who, actorRole: 'claimant',
    createdAt: s(at) || new Date(0).toISOString(), readAt: null,
  }, { audience: x.audience }));
  return { item, notices, aboveNotOnPlatform: r.aboveNotOnPlatform, unaddressed: r.unaddressed };
}

// Who may answer: an officer of the holding office (or the GC's office on any filed claim of his committee, or the
// administrator). caller = { uid, role, scopes[], committee, isAdmin }.
export function mayAnswer(claim, caller) {
  const c = claim || {};
  const a = caller || {};
  if (a.isAdmin || a.role === 'admin') return { may: true };
  const held = holdingOffice(c);
  if (!held) return { may: false, because: 'The claim is not held by an office.' };
  if (a.role === 'general_chair' && s(a.committee) && s(a.committee) === s(c.committee)) return { may: true };
  if (held === 'lc' && a.role === 'local_chair' && Array.isArray(a.scopes) && a.scopes.indexOf(s(c.scopeKey)) !== -1) return { may: true };
  return { may: false, because: 'Only the office holding this claim answers its channel.' };
}

export function validateAnswer(input, item) {
  const text = typeof (input && input.answer) === 'string' ? input.answer.trim() : '';
  const problems = [];
  if (!text) problems.push('Write the answer.');
  else if (text.length > TEXT_MAX) problems.push('The answer is longer than ' + TEXT_MAX + ' characters.');
  if (!item) problems.push('No such item.');
  else if (item.status === 'answered') problems.push('That item has already been answered.');
  return { ok: problems.length === 0, problems, value: { answer: text } };
}

// Age in whole days of an open item, from its stored time (seconds) - null when unknown, never zero.
export function openAgeDays(item, nowMs) {
  const t = item && item.at;
  const sec = t && typeof t.seconds === 'number' ? t.seconds : (t && typeof t.toMillis === 'function' ? Math.floor(t.toMillis() / 1000) : null);
  if (sec === null || !item || item.status !== 'open') return null;
  return Math.floor(((typeof nowMs === 'number' ? nowMs : Date.now()) / 1000 - sec) / 86400);
}

// ---- T5 item 9 (ruling 139): the member's verbal instruction, captured by the representative ----
const ISO = /^\d{4}-\d{2}-\d{2}$/;
export function claimantUidOf(claim) { const c = claim || {}; return s(c.claimantUid) || s(c.createdBy) || ''; }

// Who captures: an officer of the office holding the claim - the same test as answering.
export function mayCapture(claim, caller) { return mayAnswer(claim, caller); }

export function validateInstruction(input, claim, today) {
  const i = input || {};
  const problems = [];
  const text = typeof i.text === 'string' ? i.text.trim() : '';
  if (!text) problems.push('Write the instruction as the member gave it.');
  else if (text.length > TEXT_MAX) problems.push('The text is longer than ' + TEXT_MAX + ' characters.');
  const on = s(i.givenOn);
  if (!ISO.test(on)) problems.push('The date the member gave it (YYYY-MM-DD) is required.');
  else if (today && on > today) problems.push('An instruction dated in the future has not been given.');
  if (!HOW_GIVEN[s(i.how)]) problems.push('Say how it was given: by telephone, in person, or another way.');
  if (!holdingOffice(claim)) problems.push('The channel opens once the claim is filed and held by an office.');
  if (!claimantUidOf(claim)) problems.push('This claim has no claimant account to show it to. Under ruling 139 an instruction that is not surfaced to the member is not captured - link the claim to the member first.');
  return { ok: problems.length === 0, problems, value: { type: INSTRUCTION, text, givenOn: on, how: s(i.how) } };
}

export function planInstruction({ claim, claimId, input, caller, at }) {
  const c = claim || {};
  const label = s(c.claimNumber) || s(c.title) || s(claimId).slice(0, 8).toUpperCase();
  const who = s(caller && caller.name) || 'Your representative';
  const item = {
    type: INSTRUCTION, text: input.text, givenOn: input.givenOn, how: input.how,
    byUid: s(caller && caller.uid), byName: who, byRole: s(caller && caller.role),
    heldBy: holdingOffice(c), routedTo: ['claimant'], status: 'open',
  };
  const notice = {
    kind: NOTICE_KIND, audience: 'claimant', recipientUid: claimantUidOf(c), claimId: s(claimId), committee: s(c.committee), scopeKey: s(c.scopeKey),
    channel: 'in_app', title: 'Your instruction, as recorded by your representative: ' + label,
    body: who + ' recorded an instruction you gave ' + HOW_GIVEN[input.how] + ' on ' + input.givenOn + ' about claim ' + label + ':\n\n'
      + input.text + '\n\nIf this is what you said, confirm it on the claim page. If it is not, say there what you actually said - your answer is kept with it.',
    actorName: who, actorRole: s(caller && caller.role), createdAt: s(at) || new Date(0).toISOString(), readAt: null,
  };
  return { item, notices: [notice] };
}

// Who answers an item: the office for the claimant's own items; the claimant for an instruction captured about him.
export function mayAnswerItem(claim, caller, item) {
  if (item && item.type === INSTRUCTION) {
    const w = channelClaimant(claim, caller);
    return w && w.may ? { may: true } : { may: false, because: 'Only the claimant confirms or corrects an instruction recorded for him.' };
  }
  return mayAnswer(claim, caller);
}

// Where the answer's notice goes: to the claimant for his own items; to the holding office for his answer to an instruction.
export function answerNotices({ claim, claimId, item, answer, caller, at }) {
  const c = claim || {};
  const label = s(c.claimNumber) || s(c.title) || s(claimId).slice(0, 8).toUpperCase();
  const base = { kind: NOTICE_KIND, claimId: s(claimId), committee: s(c.committee), scopeKey: s(c.scopeKey), channel: 'in_app',
    actorName: s(caller && caller.name), actorRole: s(caller && caller.role), createdAt: s(at) || new Date(0).toISOString(), readAt: null };
  if (item && item.type === INSTRUCTION) {
    const aud = item.heldBy === 'gc' ? 'general_chair' : 'local_chair';
    return [Object.assign({}, base, { audience: aud, recipientUid: null,
      title: 'The claimant answered the instruction you recorded: ' + label, body: answer })];
  }
  return [Object.assign({}, base, { audience: 'claimant', recipientUid: claimantUidOf(c) || s(item && item.byUid) || null,
    title: 'Your ' + s(item && item.type) + ' was answered: ' + label, body: answer })];
}
